import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LocationOn,
  Hotel,
  AttachMoney,
  Star,
  Info,
} from '@mui/icons-material';

interface SearchResult {
  id: string;
  title: string;
  price: string;
  location: string;
  description: string;
  features: string[];
  source: string;
  sourceId: string;
  url: string;
}

interface SearchResultsProps {
  results: SearchResult[];
  onImport: (hotel: SearchResult) => void;
}

const HotelSearchResults: React.FC<SearchResultsProps> = ({ results, onImport }) => {
  const formatPrice = (price: string) => {
    const num = parseFloat(price);
    if (isNaN(num)) return price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <Grid container spacing={3}>
      {results.map((hotel) => (
        <Grid item xs={12} key={hotel.sourceId}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom>
                    {hotel.title}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationOn color="action" fontSize="small" />
                    <Typography variant="body2" color="textSecondary">
                      {hotel.location}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AttachMoney color="action" fontSize="small" />
                    <Typography variant="body2" color="textSecondary">
                      {formatPrice(hotel.price)}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    {hotel.description}
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {hotel.features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box display="flex" flexDirection="column" gap={2} height="100%">
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => onImport(hotel)}
                    >
                      Import Hotel
                    </Button>

                    <Tooltip title="View original listing">
                      <Button
                        variant="outlined"
                        fullWidth
                        href={hotel.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Source
                      </Button>
                    </Tooltip>

                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={hotel.source}
                        size="small"
                        color="secondary"
                      />
                      <Tooltip title="Source Information">
                        <IconButton size="small">
                          <Info fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default HotelSearchResults;